import { useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Grid, IconButton } from '@material-ui/core';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

import { IIcd10Code } from '~/services/api/icd10Codes/types';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';
import Input from '~/ui/components/inputs/Input';
import { IDiagnosisFormValues } from './form/types';
import getInitialValues, { emptyDiagnosisRow } from './form/getInitialValues';
import validate from './form/validate';

interface IProps {
  diagnosis: IIcd10Code | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DiagnosisModal = ({ diagnosis, onClose, onSuccess }: IProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { onCreateIcd10Codes, onUpdateIcd10Code } = useStoreActions(actions => actions.icd10Codes);
  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);

  const isEdit = useMemo(() => diagnosis !== null, [diagnosis]);

  const formMethods = useForm<IDiagnosisFormValues>({
    resolver: validate,
    defaultValues: getInitialValues(diagnosis),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'icd10Codes',
    keyName: 'rowId',
  });

  const onSubmit = async (values: IDiagnosisFormValues) => {
    try {
      setIsSaving(true);

      if (isEdit) {
        const item = values.icd10Codes[0];

        if (!item?.id) {
          setIsSaving(false);
          return;
        }

        await onUpdateIcd10Code({
          id: item.id,
          name: item.name.trim(),
          description: item.description.trim(),
        });
      } else {
        await onCreateIcd10Codes({
          icd10Codes: values.icd10Codes.map(item => ({
            name: item.name.trim(),
            description: item.description.trim(),
          })),
        });
      }

      showNotify(`Diagnosis successfully ${isEdit ? 'updated' : 'created'}`);
      await onSuccess();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    onClose();
  };

  return (
    <Modal
      open
      fullWidth
      maxWidth="md"
      title={isEdit ? 'Update Diagnosis (ICD-10)' : 'Add Diagnoses (ICD-10)'}
      onClose={handleClose}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {fields.map((field, index) => (
              <Grid item xs={12} key={field.rowId}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Input
                          name={`icd10Codes.${index}.name`}
                          label="Code"
                          required
                          register={register}
                          errors={errors}
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <Input
                          name={`icd10Codes.${index}.description`}
                          label="Description"
                          required
                          register={register}
                          errors={errors}
                          disabled={isSaving}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {!isEdit && fields.length > 1 && (
                    <Grid item>
                      <IconButton
                        color="secondary"
                        title="Remove"
                        onClick={() => remove(index)}
                        disabled={isSaving}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Grid container spacing={2}>
                {!isEdit && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => append({ ...emptyDiagnosisRow })}
                      disabled={isSaving}
                    >
                      Add More
                    </Button>
                  </Grid>
                )}
                <Grid item xs />
                <Grid item>
                  <Button
                    type="button"
                    size="large"
                    variant="outlined"
                    color="primary"
                    disabled={isSaving}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    isLoading={isSaving}
                    disabled={isSaving}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default DiagnosisModal;
